import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { AverageShop, PriceIncrease } from './types';

interface AverageShopTabProps {
  averageShop: AverageShop;
  priceIncreases?: { totalProducts: number; increases: PriceIncrease[] };
  basketViewMode: 'threshold' | 'size';
  onBasketViewChange: (mode: 'threshold' | 'size') => void;
}

export default function AverageShopTab({
  averageShop,
  priceIncreases,
  basketViewMode,
  onBasketViewChange,
}: AverageShopTabProps) {
  const navigate = useNavigate();

  if (!averageShop) {
    return <p className="text-gray-500">No average shop data available.</p>;
  }

  const shop = averageShop;
  const basket = basketViewMode === 'threshold' ? shop.thresholdBased : shop.sizeBased;
  const items = basket.items;

  if (items.length === 0) {
    return <p className="text-gray-500">No frequently purchased items found.</p>;
  }

  // Create a map of productName to price change percentage
  const priceChangeMap = new Map<string, number>();
  if (priceIncreases?.increases) {
    priceIncreases.increases.forEach((priceIncrease) => {
      priceChangeMap.set(priceIncrease.productName, priceIncrease.percentageIncrease);
    });
  }

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Unique products</p>
          <p className="mt-3 text-3xl font-semibold text-ink-700">{shop.totalUniqueProducts}</p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Basket total</p>
          <p className="mt-3 text-3xl font-semibold text-primary-600">{basket.total}</p>
          <p className="text-xs text-ink-400 mt-1">{items.length} items</p>
        </div>
        <div className="card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Average basket cost</p>
          <p className="mt-3 text-3xl font-semibold text-ink-700">£{(shop.averageBasketCost / 100).toFixed(2)}</p>
          <p className="text-xs text-ink-400 mt-1">Per shopping trip</p>
        </div>
        <div className="rounded-2xl border border-blue-100/80 bg-blue-50/70 p-5">
          <p className="text-sm font-semibold text-blue-900">💡 Insight</p>
          <p className="mt-2 text-sm text-blue-800">
            You buy {shop.actualAverageBasketSize} items on average. Showing {items.length}{' '}
            {basketViewMode === 'threshold' ? 'frequently rebought staples.' : 'top items by purchase frequency.'}
          </p>
        </div>
      </div>

      {shop.monthlyBasketTrends && shop.monthlyBasketTrends.length > 1 && (
        <section className="card space-y-5 p-6">
          <div>
            <h3 className="text-lg font-semibold text-ink-700">Monthly basket price trend</h3>
            <p className="text-sm text-ink-500 mt-1">Estimated total using the latest price for every item.</p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={shop.monthlyBasketTrends.map((trend) => ({
                ...trend,
                displayDate: trend.dateStr,
              }))}
              margin={{ left: 40, right: 20, top: 10, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="displayDate"
                angle={-45}
                textAnchor="end"
                height={80}
                style={{ fontSize: '12px' }}
              />
              <YAxis
                label={{ value: 'Total price (£)', angle: -90, position: 'insideLeft', dx: -35, dy: 50 }}
                tickFormatter={(value: number) => `£${(value / 100).toFixed(2)}`}
              />
              <Tooltip
                formatter={(value: any) =>
                  typeof value === 'number' ? `£${(value / 100).toFixed(2)}` : value
                }
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="totalPricePence"
                name="Basket total"
                stroke="#2563eb"
                strokeWidth={2.5}
                dot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </section>
      )}

      <section className="card overflow-hidden">
        <div className="space-y-4 border-b border-ink-100/70 p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-ink-700">Your shopping basket</h3>
              <p className="text-sm text-ink-500 mt-1">{basket.description}</p>
              <p className="badge-muted mt-3">📅 Based on the last 12 months.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onBasketViewChange('threshold')}
                className={`pill-tab ${
                  basketViewMode === 'threshold' ? 'bg-primary-600 text-white shadow-sm ring-1 ring-primary-400' : ''
                }`}
              >
                Core staples (≥{shop.thresholdBased.minFrequencyThreshold}x)
              </button>
              <button
                onClick={() => onBasketViewChange('size')}
                className={`pill-tab ${
                  basketViewMode === 'size' ? 'bg-primary-600 text-white shadow-sm ring-1 ring-primary-400' : ''
                }`}
              >
                Top {Math.ceil(shop.actualAverageBasketSize)} items
              </button>
            </div>
          </div>
        </div>
        <div className="table-card border-transparent">
          <table className="table-default">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-center">Purchases</th>
                <th className="text-center group relative">
                  <span className="cursor-help">Avg qty</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    Average quantity per purchase
                  </div>
                </th>
                <th className="text-center">Unit price</th>
                <th className="text-right group relative">
                  <span className="cursor-help">Total/shop</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    Average unit price × average quantity
                  </div>
                </th>
                <th className="text-center group relative">
                  <span className="cursor-help">Price change %</span>
                  <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                    Percentage change (3-month averages)
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100/80">
              {items.map((item) => {
                const priceChangePercent = priceChangeMap.get(item.productName);
                return (
                  <tr key={item.productName}>
                    <td className="font-medium text-ink-700">
                      <button
                        onClick={() => navigate(`/price-history?product=${encodeURIComponent(item.productName)}`)}
                        className="text-left text-accent-600 underline decoration-dotted underline-offset-4 transition hover:text-accent-500"
                        title="Click to view full price history"
                      >
                        {item.productNameOriginal}
                      </button>
                    </td>
                    <td className="text-center text-ink-500">{item.frequency}x</td>
                    <td className="text-center text-ink-500">{item.avgQuantity}</td>
                    <td className="text-center text-ink-500">{item.avgPrice}</td>
                    <td className="text-right font-medium text-ink-600">
                      £{((item.avgPricePence * item.avgQuantity) / 100).toFixed(2)}
                    </td>
                    <td
                      className={`text-center text-sm font-semibold ${
                        priceChangePercent !== undefined && priceChangePercent !== 0
                          ? priceChangePercent > 0
                            ? 'text-red-600'
                            : 'text-green-600'
                          : 'text-ink-300'
                      }`}
                    >
                      {priceChangePercent !== undefined && priceChangePercent !== 0
                        ? `${priceChangePercent > 0 ? '+' : ''}${priceChangePercent}%`
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {basket.substitutes && basket.substitutes.length > 0 && (
        <section className="card overflow-hidden">
          <div className="space-y-5 border-b border-ink-100/70 p-6">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔄</span>
              <div>
                <h3 className="text-lg font-semibold text-ink-700">Likely substitutes</h3>
                <p className="text-sm text-ink-500 mt-1">
                  Items that never appeared alongside your staples—they&apos;re the alternatives you switch between.
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-purple-100/80 bg-purple-50/70 p-4">
              <p className="text-sm font-semibold text-purple-900">💡 How this works</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-purple-800">
                <li><strong>Co-purchase analysis:</strong> We detect items that never appear with your main basket.</li>
                <li><strong>Substitutes:</strong> You typically buy one or the other, not both.</li>
                <li><strong>Use it for planning:</strong> Track which alternative is better value this month.</li>
              </ul>
            </div>
          </div>
          <div className="table-card border-transparent">
            <table className="table-default">
              <thead>
                <tr>
                  <th>Substitute product</th>
                  <th className="text-center">Purchases</th>
                  <th className="text-center group relative">
                    <span className="cursor-help">Avg qty</span>
                    <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                      Average quantity per purchase
                    </div>
                  </th>
                  <th className="text-center">Unit price</th>
                  <th className="text-right group relative">
                    <span className="cursor-help">Total/shop</span>
                    <div className="pointer-events-none absolute bottom-full left-1/2 hidden -translate-x-1/2 transform whitespace-nowrap rounded-md bg-ink-700 px-2 py-1 text-xs text-white shadow-sm group-hover:block">
                      Average unit price × average quantity
                    </div>
                  </th>
                  <th className="text-center">Substitute for</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100/80">
                {basket.substitutes.map((substitute) => (
                  <tr key={substitute.productName}>
                    <td className="font-medium text-ink-700">
                      <button
                        onClick={() => navigate(`/price-history?product=${encodeURIComponent(substitute.productName)}`)}
                        className="text-left text-accent-600 underline decoration-dotted underline-offset-4 transition hover:text-accent-500"
                        title="Click to view full price history"
                      >
                        {substitute.productNameOriginal}
                      </button>
                    </td>
                    <td className="text-center text-ink-500">{substitute.frequency}x</td>
                    <td className="text-center text-ink-500">{substitute.avgQuantity}</td>
                    <td className="text-center text-ink-500">{substitute.avgPrice}</td>
                    <td className="text-right font-medium text-ink-600">
                      £{((substitute.avgPricePence * substitute.avgQuantity) / 100).toFixed(2)}
                    </td>
                    <td className="text-center">
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-xs font-semibold text-purple-700">
                        {substitute.substituteForOriginal}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

